import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { HELP_CONTENT, sectionSearchText } from '../data/helpContent';
import './Help.css';

function renderBlock(block, idx) {
  switch (block.type) {
    case 'p':
      return <p key={idx}>{block.text}</p>;
    case 'ul':
      return (
        <ul key={idx}>
          {block.items.map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={idx}>
          {block.items.map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ol>
      );
    case 'h3':
      return <h3 key={idx}>{block.text}</h3>;
    case 'tip':
      return <div key={idx} className="help-tip">{block.text}</div>;
    case 'note':
      return <div key={idx} className="help-note">{block.text}</div>;
    default:
      return null;
  }
}

function Help({ onLogout, onFeedback }) {
  const [query, setQuery] = useState('');
  const [highlightId, setHighlightId] = useState(null);
  const location = useLocation();
  const sectionRefs = useRef({});

  const q = query.trim().toLowerCase();

  // Filter sections by search. When the query is empty, show everything.
  const filteredCategories = useMemo(() => {
    if (!q) return HELP_CONTENT;
    return HELP_CONTENT
      .map((cat) => ({
        ...cat,
        sections: cat.sections.filter((s) => sectionSearchText(s).includes(q)),
      }))
      .filter((cat) => cat.sections.length > 0);
  }, [q]);

  const noMatches = filteredCategories.length === 0;

  // Handle deep-linking via URL hash (e.g. /help#send-job-offer).
  useEffect(() => {
    const id = location.hash.replace('#', '');
    if (!id) return;
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setHighlightId(id);
      const timer = setTimeout(() => setHighlightId(null), 2500);
      return () => clearTimeout(timer);
    }
  }, [location.hash, filteredCategories]);

  const jumpTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setHighlightId(id);
      setTimeout(() => setHighlightId(null), 2500);
      // Update URL hash so the user can copy/share the link.
      window.history.replaceState(null, '', `#${id}`);
    }
  };

  return (
    <div>
      <Navigation onLogout={onLogout} onFeedback={onFeedback} />
      <div className="help-layout">
        <aside className="help-sidebar">
          <input
            type="text"
            className="help-search"
            placeholder="Search the manual…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          {filteredCategories.map((cat) => (
            <div key={cat.category} className="help-toc-category">
              <div
                className="help-toc-category-title"
                style={cat.color ? { color: cat.color } : undefined}
              >
                {cat.category}
              </div>
              <ul className="help-toc-list">
                {cat.sections.map((s) => (
                  <li
                    key={s.id}
                    className={highlightId === s.id ? 'active' : ''}
                    onClick={() => jumpTo(s.id)}
                  >
                    {s.title}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {noMatches && (
            <div className="help-empty">No matches for "{query}".</div>
          )}
        </aside>

        <main className="help-main">
          <h1>User Manual</h1>
          <p className="help-intro">
            Use the search box on the left to jump to a specific topic, or scroll through the sections.
          </p>

          {noMatches ? (
            <div className="help-empty">
              Nothing matches "{query}". Try a shorter word or a different phrase.
            </div>
          ) : (
            filteredCategories.map((cat) =>
              cat.sections.map((s) => (
                <section
                  key={s.id}
                  id={s.id}
                  ref={(el) => { sectionRefs.current[s.id] = el; }}
                  className={`help-section${highlightId === s.id ? ' highlight' : ''}`}
                  style={cat.color ? { borderLeft: `4px solid ${cat.color}` } : undefined}
                >
                  <h2 className="help-section-title">{s.title}</h2>
                  <span
                    className="help-section-category"
                    style={cat.color ? { color: cat.color } : undefined}
                  >
                    {cat.category}
                  </span>
                  {s.body.map(renderBlock)}
                </section>
              ))
            )
          )}
        </main>
      </div>
    </div>
  );
}

export default Help;
